'use client';

import { cn } from '@/lib/utils';
import * as React from 'react';
import {
    FormControl,
    FormDescription,
    FormItem,
    FormLabel,
    FormMessage,
} from '../ui/form';

type FormItemControllerProps = {
    label?: React.ReactNode;
    description?: React.ReactNode;

    error?: React.ReactNode;
    required?: boolean;
    colon?: boolean;
    showTextError?: boolean;

    wrapperClassName?: string;
    labelClassName?: string;
    descriptionClassName?: string;
    errorClassName?: string;

    layout?: 'vertical' | 'inline';
};

const FormItemController = (
    props: React.PropsWithChildren<FormItemControllerProps>
) => {
    const {
        children,
        colon = true,
        showTextError = true,
        layout = 'vertical',
        description,
        error,
        label,
        labelClassName,
        required,
        wrapperClassName,
        descriptionClassName,
        errorClassName,
    } = props;

    if (layout === 'inline') {
        return (
            <FormItem
                className={cn(
                    'mb-3 flex gap-x-2 max-xl:flex-col max-xl:gap-y-1',
                    wrapperClassName
                )}
            >
                {label && (
                    <FormLabel
                        className={cn('max-xl:w-full xl:mr-2', labelClassName)}
                    >
                        <span
                            className={`leading-6 xl:leading-10 ${required && 'required'}`}
                        >
                            {label}
                            {colon && ':'}
                        </span>
                    </FormLabel>
                )}
                <div className='!mt-0 flex-1 max-xl:w-full'>
                    <FormControl>{children}</FormControl>

                    {description && (
                        <FormDescription
                            className={cn('', descriptionClassName)}
                        >
                            {description}
                        </FormDescription>
                    )}
                    {showTextError && (
                        <FormMessage
                            className={cn('mt-1 text-xs', errorClassName)}
                        >
                            {error}
                        </FormMessage>
                    )}
                </div>
            </FormItem>
        );
    }

    return (
        <FormItem className={cn('mb-2 w-full', wrapperClassName)}>
            {label && (
                <FormLabel
                    className={cn(
                        '',
                        labelClassName,
                        `${required && 'required'}`
                    )}
                >
                    {label}
                    {colon && ':'}
                </FormLabel>
            )}
            <FormControl>{children}</FormControl>
            {description && (
                <FormDescription className={cn('', descriptionClassName)}>
                    {description}
                </FormDescription>
            )}
            {showTextError && (
                <FormMessage className={cn('mt-1 text-xs', errorClassName)}>
                    {error}
                </FormMessage>
            )}
        </FormItem>
    );
};

export default React.memo(FormItemController);
